/**
 * Admin sidebar navigation configuration.
 * Shared between AdminSidebar (desktop) and AdminDropdown/AdminMobileMenu (header).
 */
import type { LucideIcon } from "lucide-react";
import {
  LayoutDashboard,
  Handshake,
  Package,
  FileText,
  Users,
  Building2,
  SearchCheck,
  MapPin,
  Leaf,
  Calculator,
  Library,
  Euro,
  Send,
  Mail,
  Activity,
  Clock,
  ClipboardList,
  Lightbulb,
  Workflow,
  Bot,
} from "lucide-react";
import { routes } from "@/lib/routes";

export interface AdminNavItem {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
}

export interface AdminNavGroup {
  id: string;
  label: string;
  items: AdminNavItem[];
}

/** Dashboard — always shown standalone at top of sidebar */
export const ADMIN_DASHBOARD_ITEM: AdminNavItem = {
  id: "dashboard",
  label: "Dashboard",
  href: routes.admin.dashboard,
  icon: LayoutDashboard,
};

/** Grouped navigation sections for the sidebar */
export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    id: "marktplatz",
    label: "Marktplatz",
    items: [
      { id: "sales", label: "Vertriebs-Cockpit", href: routes.admin.sales, icon: ClipboardList },
      { id: "lead-agent", label: "Lead-Agent", href: routes.admin.leadAgent.list, icon: Bot },
      {
        id: "opportunities",
        label: "Mögliche Vorgänge",
        href: routes.admin.deals.opportunities,
        icon: Lightbulb,
      },
      { id: "deals", label: "Alle Vorgänge", href: routes.admin.deals.list, icon: Handshake },
      { id: "offerings", label: "Alle Angebote", href: routes.admin.offerings.list, icon: Package },
      { id: "requests", label: "Alle Anfragen", href: routes.admin.requests.list, icon: FileText },
      { id: "activity", label: "Letzte Aktivitäten", href: routes.admin.activity, icon: Clock },
    ],
  },
  {
    id: "verwaltung",
    label: "Verwaltung",
    items: [
      { id: "users", label: "Alle Benutzer", href: routes.admin.users.list, icon: Users },
      {
        id: "organizations",
        label: "Organisationen",
        href: routes.admin.organizations.list,
        icon: Building2,
      },
    ],
  },
  {
    id: "kommunikation",
    label: "Kommunikation",
    items: [
      { id: "campaigns", label: "Kampagnen", href: routes.admin.campaigns.list, icon: Send },
      { id: "emails", label: "E-Mail Templates", href: routes.admin.emails, icon: Mail },
    ],
  },
  {
    id: "konfiguration",
    label: "Konfiguration",
    items: [
      { id: "plz", label: "PLZ-Verwaltung", href: routes.admin.plz, icon: MapPin },
      { id: "naturraeume", label: "Naturräume", href: routes.admin.naturraeume, icon: Leaf },
      {
        id: "registry",
        label: "Kompensationsregister",
        href: routes.admin.registry,
        icon: Library,
      },
      {
        id: "instruments",
        label: "Kompensationsmethoden",
        href: routes.admin.instruments,
        icon: Calculator,
      },
      { id: "price-lists", label: "Preislisten", href: routes.admin.priceLists, icon: Euro },
    ],
  },
  {
    id: "entwicklung",
    label: "Entwicklung",
    items: [
      {
        id: "match-debugger",
        label: "Match-Debugger",
        href: routes.admin.matchDebugger,
        icon: SearchCheck,
      },
      { id: "events", label: "Events", href: routes.admin.events, icon: Activity },
      { id: "process-lab", label: "Prozesslabor", href: routes.admin.processLab, icon: Workflow },
    ],
  },
];

/** Flat list of all items — used by AdminDropdown and AdminMobileMenu */
export const ADMIN_ALL_ITEMS: AdminNavItem[] = [
  ADMIN_DASHBOARD_ITEM,
  ...ADMIN_NAV_GROUPS.flatMap((g) => g.items),
];
